import * as React from 'react';

import { RichText } from '@/design';
import type {
  IInternalFieldActor,
  IInternalFieldEvent,
  IInternalFieldHistory,
  IInternalValueOption,
  InternalEventValue,
  InternalFieldType,
  InternalPayload
} from '../../api/internal-field-contracts';
import { internalFieldsApi } from '../../api/internal-fields-api';
import { absent } from '../../components/account-facts';
import { withBatch } from '../../components/history-batch';
import { HistoryPopover, type IHistoryPage, type IHistoryRow } from '../../components/history-popover';
import { internalValueText, isInternalFieldType } from '../../internal-field-values';

/**
 * A staff field's history under Veritant research (the advisor record page's F2, W2; the plan's
 * R13): survey-api's `…/participants/{id}/internal-fields/{code}/events`, turned into the kit's
 * `HistoryPopover` rows, beside every field's pencil — and on every read-only field too, since a
 * derived value's imported row is history worth seeing.
 *
 * - **A row is one event as sent**, newest first, never sorted: When, Who, What ("Set",
 *   "Cleared", any other kind as spelled), From, To. **No Reason column** — an internal write
 *   takes none, and the popover draws the column only where a row carries one.
 * - **Who**: an account by its email — "an account since removed" when survey-api has no email
 *   for it, and the role as spelled for an event no account made (an import with no Salesforce
 *   actor); a Salesforce actor "<name> (Salesforce)", or "Salesforce" with no name.
 * - **A value is read by its own `t`**; a Salesforce row's `{v}`, which names no type, by the
 *   field's — and only where `v` is that type's JSON kind. Anything else is drawn as written,
 *   never dropped and never coerced: a code is labelled by the field's options, retired ones
 *   included through the stored value's own labels. A rich-text value goes through `RichText`.
 * - **A row written in a batch names it** under *What* (`withBatch`, SR-230's §7): a list's save,
 *   a mass update, an import or its reversal, with the batch's note — one function for this, the
 *   figures' and the placements' histories.
 * - **What is stored now is drawn above the rows**, "Stored now: …" per stored row — more than one
 *   only for a field recorded per cycle or per response — and never merged into them: survey-api
 *   sends it as the rows stand, not derived, and an imported value has no event at all.
 *
 * Rejected: drawing an event's `responseId` / `cycleId` — the route sends ids, which name nothing a
 * reader recognises, and the household's responses are a section away.
 */

/** Ten changes a page — a popover's, not a list's (survey-api's default is fifty). */
const HISTORY_PAGE_SIZE = 10;

const KIND_WORDS: Record<string, string> = {
  value_set: 'Set',
  value_cleared: 'Cleared'
};

/** Whether `v` is the JSON kind a payload of that type holds (`IInternalValueByType`). */
function holdsKind(type: InternalFieldType, v: unknown): boolean {
  switch (type) {
    case 'int':
    case 'decimal':
    case 'money':
    case 'percent':
      return typeof v === 'number';
    case 'bool':
      return typeof v === 'boolean';
    case 'multi_select':
      return Array.isArray(v) && v.every((code) => typeof code === 'string');
    default:
      return typeof v === 'string';
  }
}

/** The payload a history value is, read by its own `t` or else by the field's — or null where
 *  neither says how `v` reads. */
function asPayload(value: InternalEventValue, fieldType: Nullable<string>): Nullable<InternalPayload> {
  const type = value.t ?? fieldType;
  if (type == null || !isInternalFieldType(type) || !holdsKind(type, value.v)) return null;
  return { t: type, v: value.v } as InternalPayload;
}

/** One value as a history cell draws it. */
function HistoryValue({
  value,
  fieldType,
  options
}: {
  value: Nullable<InternalEventValue>;
  fieldType: Nullable<string>;
  options: readonly IInternalValueOption[];
}) {
  // A value with no `v` holds nothing to draw as written: `JSON.stringify` would answer
  // `undefined`, a blank cell.
  if (value === null || value.v === undefined) return absent('—');
  const payload = asPayload(value, fieldType);
  if (payload === null) {
    return <>{typeof value.v === 'string' ? value.v : JSON.stringify(value.v)}</>;
  }
  if (payload.t === 'rich_text') return <RichText html={payload.v} />;
  return <>{internalValueText(payload, options)}</>;
}

function who(actor: IInternalFieldActor): React.ReactNode {
  if (actor.kind === 'salesforce') {
    return actor.name ? `${actor.name} (Salesforce)` : 'Salesforce';
  }
  if (actor.kind === 'account') {
    if (actor.email) return actor.email;
    if (!actor.accountId && actor.role) return absent(`No account (${actor.role})`);
    return absent('an account since removed');
  }
  return actor.kind;
}

/** survey-api's history document as the popover's page (R13). */
export function internalHistoryPage(
  history: IInternalFieldHistory,
  fieldType: Nullable<string>,
  options: readonly IInternalValueOption[]
): IHistoryPage {
  const cell = (value: Nullable<InternalEventValue>) => (
    <HistoryValue value={value} fieldType={fieldType} options={options} />
  );
  const items = history.items.map(
    (event: IInternalFieldEvent, index): IHistoryRow => ({
      // An event carries no id; its place in the page is unique within the page.
      key: `${history.page}:${index}`,
      at: event.at,
      who: who(event.actor),
      what: withBatch(KIND_WORDS[event.kind] ?? event.kind, event.batch),
      from: cell(event.old),
      to: cell(event.new)
    })
  );
  const above =
    history.stored.length === 0 ? undefined : (
      <div className="grid gap-1" data-stored>
        {history.stored.map((row, index) => (
          <div key={index} className="flex flex-wrap items-baseline gap-x-1.5">
            <span className="text-muted-foreground">{'Stored now: '}</span>
            {cell(row.value)}
          </div>
        ))}
      </div>
    );
  return { items, page: history.page, pageSize: history.pageSize, total: history.total, above };
}

/** The history button of one field about one member. */
export function ResearchHistory({
  participantId,
  code,
  label,
  name,
  fieldType,
  options
}: {
  participantId: string;
  code: string;
  label: string;
  /** The member's name — "History of <label> for <name>". */
  name: string;
  /** The schema's type, or null for a field the schema no longer offers. */
  fieldType: Nullable<string>;
  /** Labels for its codes — the stored value's own first, then the schema's. */
  options: readonly IInternalValueOption[];
}) {
  return (
    <HistoryPopover
      label={label}
      name={name}
      emptyText="No change to this field is recorded."
      read={async (page, signal) =>
        internalHistoryPage(
          await internalFieldsApi.history(participantId, code, page, HISTORY_PAGE_SIZE, signal),
          fieldType,
          options
        )
      }
    />
  );
}
